// src/pages/reports/IrAnalysis/ReportsIrTrTable.tsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TableSortLabel,
  Paper,
} from "@mui/material";
import DropDownFilter, { Option } from "./DropDownFilter";
import PaginationReports from "../../brands/PaginationReports";
import { exportIrTransReport, exportIrTransReport_csv } from "../api";
import Export from "../../../../src/assests/images/export.svg";
import Sorting from "../../../../src/assests/images/Sorting.svg";
import { getToken } from "../../../utils/utils";

// Strict IR Transaction Report columns (must match backend keys EXACTLY)
const columns: Option[] = [
  { label: "Transaction Date", value: "Transaction Date" },
  { label: "Region", value: "Region" },
  { label: "ASM Name", value: "ASM Name" },
  { label: "Area Code", value: "Area Code" },
  { label: "TSE Name", value: "TSE Name" },
  { label: "Territory Code", value: "Territory Code" },
  { label: "PMI Entity Code", value: "PMI Entity Code" },
  { label: "Outlet Name", value: "Outlet Name" },
  { label: "City", value: "City" },
  { label: "Outlet Category", value: "Outlet Category" },
  { label: "Outlet Latitude", value: "Outlet Latitude" },
  { label: "Outlet Longitude", value: "Outlet Longitude" },
  { label: "Image Capture Latitude", value: "Image Capture Latitude" },
  { label: "Image Capture Longitude", value: "Image Capture Longitude" },
  { label: "Distance Of Image Capture", value: "Distance Of Image Capture" },
  { label: "Task ID", value: "Task ID" },
  { label: "Published ID", value: "Published ID" },
  { label: "Activity ID", value: "Activity ID" },
  { label: "Image Link", value: "Image Link" },
  { label: "Image Quality", value: "Image Quality" },
  { label: "Rejection Reason", value: "Rejection Reason" },
  { label: "Task Parameter Response", value: "Task Parameter Response" },
  { label: "Self Facings", value: "Self Facings" },
  { label: "Competition Facings", value: "Competition Facings" },
  { label: "Others Facings", value: "Others Facings" },
];

// Default visible columns
const DEFAULT_VISIBLE = [
  "Transaction Date",
  "Region",
  "ASM Name",
  "TSE Name",
  "PMI Entity Code",
  "Outlet Name",
  "City",
];

interface ReportsIrTrTableProps {
  searchQuery: string;
  appliedFilters?:
    | {
        city?: string | string[];
        locations?: string[] | string;
        outlet_category?: string[]; // accounts.flex_parameter_1
        pmi_entity_code?: string[]; // accounts.dte_account_id
        outlet_name?: string[];     // accounts.name
        /** ✅ include the date fields from the filter modal */
        transaction_from?: string | null; // "YYYY-MM-DD"
        transaction_to?: string | null;   // "YYYY-MM-DD"
      }
    | null;
  onBackendFilterOptions?: (opts: {
    available_cities: string[];
    available_outlet_names: string[];
    available_pmi_codes: string[];
    available_outlet_categories: string[];
  }) => void;
}

const ReportsIrTrTable: React.FC<ReportsIrTrTableProps> = ({
  searchQuery,
  appliedFilters = null,
  onBackendFilterOptions,
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const totalPages = Math.ceil(totalRows / itemsPerPage);

  const [selectedCols, setSelectedCols] = useState<string[]>(DEFAULT_VISIBLE);

  const [orderBy, setOrderBy] = useState<string>("Outlet Name");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState(false);

  // Reset page when filters/search change
  useEffect(() => {
    setPage(1);
  }, [appliedFilters, searchQuery]);

  // Allow free unselect; if empty, snap back to defaults.
  const handleSelectedColsChange = (vals: string[]) => {
    const allowed = new Set(columns.map((c) => c.value));
    let next = (vals || []).filter((v) => allowed.has(v));
    if (next.length === 0) next = [...DEFAULT_VISIBLE];
    setSelectedCols(next);
  };

  // STRICT payload matching backend filters
  const buildPayload = () => {
    const payload: Record<string, any> = {
      page,
      limit: itemsPerPage,
    };

    if (appliedFilters?.city) payload.city = appliedFilters.city;

    if (
      appliedFilters?.locations &&
      (Array.isArray(appliedFilters.locations)
        ? appliedFilters.locations.length
        : String(appliedFilters.locations).length)
    ) {
      payload.locations = appliedFilters.locations;
    }

    if (appliedFilters?.outlet_category?.length)
      payload.outlet_category = appliedFilters.outlet_category;

    if (appliedFilters?.pmi_entity_code?.length)
      payload.pmi_entity_code = appliedFilters.pmi_entity_code;

    if (appliedFilters?.outlet_name?.length)
      payload.outlet_name = appliedFilters.outlet_name;

    if (searchQuery && searchQuery.trim()) {
      payload.search = searchQuery.trim();
    }

    /** ✅ forward the date filters exactly as BE expects */
    if (typeof appliedFilters?.transaction_from === "string" && appliedFilters.transaction_from) {
      payload.transaction_from = appliedFilters.transaction_from;
    }
    if (typeof appliedFilters?.transaction_to === "string" && appliedFilters.transaction_to) {
      payload.transaction_to = appliedFilters.transaction_to;
    }

    return payload;
  };

  // Fetch table data (list)
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = getToken();
        const payload = buildPayload();

        const response = await exportIrTransReport(payload, token!);

        const resultsArray = response?.results ?? [];
        const totalRowsValue = response?.total_rows ?? 0;

        setRows(resultsArray);
        setTotalRows(totalRowsValue);

        if (onBackendFilterOptions) {
          onBackendFilterOptions({
            available_cities: Array.isArray(response?.available_cities)
              ? response.available_cities
              : [],
            available_outlet_names: Array.isArray(response?.available_outlet_names)
              ? response.available_outlet_names
              : [],
            available_pmi_codes: Array.isArray(response?.available_pmi_codes)
              ? response.available_pmi_codes
              : [],
            available_outlet_categories: Array.isArray(response?.available_outlet_categories)
              ? response.available_outlet_categories
              : [],
          });
        }
      } catch (err) {
        console.error("Failed to fetch IR Transaction report:", err);
        setRows([]);
        setTotalRows(0);
        if (onBackendFilterOptions) {
          onBackendFilterOptions({
            available_cities: [],
            available_outlet_names: [],
            available_pmi_codes: [],
            available_outlet_categories: [],
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedFilters, searchQuery]);

  // Sorting (client-side)
  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const aVal = a?.[orderBy] ?? "";
      const bVal = b?.[orderBy] ?? "";
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return order === "asc" ? -1 : 1;
      if (aStr > bStr) return order === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, orderBy, order]);

  // CSV Export (STRICT)
  const handleExport = async () => {
    try {
      const token = getToken();
      const payload = {
        ...buildPayload(),
        columns: selectedCols, // project only selected columns
        limit: "all",
        export: true,
      };

      const blob = await exportIrTransReport_csv(payload, token, true);

      if (blob instanceof Blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `ir_transactions_${new Date().toISOString().slice(0, 10)}.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Unexpected response from exportIrTransReport_csv:", blob);
      }
    } catch (err) {
      console.error("IR Transactions Export failed:", err);
    }
  };

  // Render helper (format Transaction Date nicely)
  const renderCell = (colValue: string, raw: any) => {
    if (colValue === "Transaction Date" && raw) {
      const d = new Date(raw);
      return isNaN(d.getTime()) ? String(raw) : d.toLocaleString();
    }
    // numeric geo/distance pretty-print
    const isGeo =
      colValue.includes("Latitude") ||
      colValue.includes("Longitude") ||
      colValue.includes("Distance");
    if (isGeo && typeof raw === "number") return raw.toFixed(4);

    return raw !== null && raw !== undefined && raw !== "" ? String(raw) : "-";
  };

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "10px",
        border: "1px solid #cfcfcf",
        margin: "30px 32px",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: "25px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>
          {loading ? "Loading…" : `Total ${totalRows} Records`}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ marginRight: "20px" }}>
            <DropDownFilter
              placeholder="Select Columns"
              options={columns}
              value={selectedCols}
              onChange={handleSelectedColsChange}
              disabled={loading}
            />
          </Box>

          <Button
            variant="contained"
            sx={{
              textTransform: "capitalize",
              backgroundColor: "#fff",
              fontSize: "14px",
              fontWeight: 500,
              height: "40px",
              padding: "16px",
              borderRadius: "10px",
              color: "#C8172F",
              border: "1px solid #C8172F",
              boxShadow: "none",
            }}
            startIcon={<img src={Export} alt="export" />}
            onClick={handleExport}
            disabled={loading || totalRows === 0}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ boxShadow: "none", opacity: loading ? 0.6 : 1 }}>
        <Table>
          <TableHead sx={{ background: "#EEF4FF" }}>
            <TableRow>
              {columns
                .filter((col) => selectedCols.includes(col.value))
                .map((col) => (
                  <TableCell key={col.value}>
                    <TableSortLabel
                      active={orderBy === col.value}
                      direction={orderBy === col.value ? order : "asc"}
                      onClick={() => handleSort(col.value)}
                      IconComponent={() => (
                        <img src={Sorting} alt="sort" style={{ width: 16, height: 16 }} />
                      )}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedRows.length > 0 ? (
              sortedRows.map((row, idx) => (
                <TableRow key={idx}>
                  {columns
                    .filter((col) => selectedCols.includes(col.value))
                    .map((col) => {
                      const raw = row?.[col.value];
                      const display = renderCell(col.value, raw);
                      return (
                        <TableCell
                          key={col.value}
                          sx={{
                            maxWidth: 260,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={typeof display === "string" ? display : undefined}
                        >
                          {display}
                        </TableCell>
                      );
                    })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={selectedCols.length} align="center">
                  {loading ? "Loading…" : "No data available"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <PaginationReports
        page={page}
        limit={itemsPerPage}
        totalPages={totalPages}
        setPage={setPage}
      />
    </Box>
  );
};

export default ReportsIrTrTable;
